/**
 * Pronoun resolution against the caller-curated recency list (plan §4).
 *
 * @remarks
 * v1 policy, deliberately simple: a pronoun resolves to the FIRST ref in the
 * most-recent-first list that may answer to it (plural nuance deferred). An
 * empty list, or one with nothing that may answer, means the pronoun has
 * nothing to resolve against, and the clause honestly fails as
 * `entity_not_found` naming the pronoun. The parser applies no world
 * knowledge of its own; the caller curates the list (architecture.md §4.4).
 * Chained-clause recency threading (clause 1's objects feeding clause 2's
 * view) is M6's orchestration work, not this module's.
 *
 * D245 (the Meridian play-test's A6): what may answer is the catalog's to
 * say. An entry that declares its pronoun (`ParseCandidateEntity.pronoun`)
 * answers to that word alone — `it` after `examine marrow` passes over the
 * woman to the last THING spoken of, and `her` reaches her. An entry that
 * declares none answers to every word, as all of them did before, so a
 * catalog without pronouns resolves exactly as it always has. And `him` and
 * `her` PREFER an entry that declares them: a thing never declares a gender,
 * so a gendered word is about a person whenever one is in the list, and
 * falls back to the most recent thing only when none is (D226's reading,
 * for a pack whose people declare nothing).
 *
 * Every successful resolution is surfaced as a {@link PronounResolution} so
 * UIs can echo what "it" meant, and costs ×0.9 confidence (plan §6.1, applied
 * by the ranking stage).
 *
 * @packageDocumentation
 */

import { type PronounCanonicalToken } from '../grammar/canonicalize-tokens.js';
import { type PronounWord } from '../lexicon/builtin-function-words.js';
import { type EntityRef, type ParseCandidateEntity, type PronounResolution } from '../types.js';

/** The outcome of resolving one pronoun (result-typed; an empty recency list is data, not an error). */
export type PronounResolutionOutcome =
	| { readonly pronounText: string; readonly type: 'unresolved' }
	| { readonly resolution: PronounResolution; readonly type: 'resolved' };

/** D245: the words only a person takes — they prefer an entry that declares them over a thing that declares nothing. */
const PERSONAL_PRONOUN_WORDS: ReadonlySet<PronounWord> = new Set(['her', 'him']);

/**
 * Resolve one pronoun token against the recency list.
 *
 * @param pronounToken - The pronoun as canonicalized from the clause
 * @param recentEntityRefs - Most-recent-first refs; the caller curates, the parser trusts
 * @param entities - The per-parse catalog, whose entries may declare their pronoun (D245)
 * @returns The resolution, or `unresolved` when nothing in the list may answer to the word
 */
export const resolvePronoun = (
	pronounToken: PronounCanonicalToken,
	recentEntityRefs: readonly EntityRef[],
	entities: readonly ParseCandidateEntity[],
): PronounResolutionOutcome => {
	const declaredPronouns = new Map(entities.flatMap((entity) => (entity.pronoun === undefined ? [] : [[entity.entityRef, entity.pronoun] as const])));
	const word = pronounToken.pronoun;
	const answering = recentEntityRefs.filter((entityRef) => {
		const declared = declaredPronouns.get(entityRef);

		return declared === undefined || declared === word;
	});
	const referent =
		(PERSONAL_PRONOUN_WORDS.has(word) ? answering.find((entityRef) => declaredPronouns.get(entityRef) === word) : undefined) ?? answering[0];

	if (referent === undefined) {
		return { pronounText: word, type: 'unresolved' };
	}

	return {
		resolution: {
			entityRef: referent,
			pronoun: word,
			sourceSpan: pronounToken.sourceSpan,
		},
		type: 'resolved',
	};
};
